import React from 'react';
import { Dispatch } from 'redux';
import { connect } from "react-redux";
import { IApplicationState, RootActionCreators } from '../../../store';
import { RouteComponentProps } from 'react-router';
import { IReportDataObject, IReportData } from '../../../entities/reports/IReportData';
import { IData as IClientDetail } from '../../../store/client/clientItem';
import { IData as IClientFile } from '../../../store/client/clientFilesList';
import { INavigations, mapNavigationsToProps, ClientNavigation, ClientNavigationPage } from '../../organisms/client/ClientNavigation';
import { PageHeader } from '../../organisms/PageHeader';
import { ClientDetail } from '../../organisms/client/ClientDetail';
import { FetchState } from '../../../entities/FetchState';
import { RefreshButton } from '../../atoms/buttons/RefreshButton';
import { EditButton } from '../../atoms/buttons/EditButton';
import { DeleteButton } from '../../atoms/buttons/DeleteButton';
import { useTranslation } from 'react-i18next';
import ErrorMessage from '../../organisms/ErrorMessage';
import { DeletingMessage } from '../../organisms/DeletingMessage'; 

interface IMatchParams {
    id: string;
}
interface IPropsFromParams extends RouteComponentProps<IMatchParams> { }

// Standard Properties from parent component
interface IProps { }

// Outgoing functions to parent component
interface IDispatch {}

// Properties from State 
interface IStateProps {
  details: IReportDataObject<IClientDetail>;
  deleter: IReportDataObject<IClientDetail>;
  clientFiles: IReportData<IClientFile>;
}
const mapStateProps = (state: IApplicationState): IStateProps => ({
    details: state.client.clientItem.Item,
    deleter: state.client.clientItem.DeleteItem,
    clientFiles: state.client.clientFilesList.ClientFilesList
});

// Outgoing functions to Dispatch
interface IDispatchProps {
    navigations: INavigations;
    onLoadData: (id: string) => void;
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
    onAnonymize: (id: string) => void;
    onResetDeleteState: () => void;
    onLoadFiles: (clientId: string) => void;
 }
const mapDispatchProps = (dispatch: Dispatch): IDispatchProps => ({
    navigations: mapNavigationsToProps(dispatch),
    onLoadData: (id: string) => dispatch(RootActionCreators.Client.clientItem.FetchData(id)),
    onEdit: (id: string) => dispatch(RootActionCreators.Navigate.ToClientEdit(id)),
    onDelete: (id: string) => dispatch(RootActionCreators.Client.clientItem.DeleteData(id)),
    onAnonymize: (id: string) => dispatch(RootActionCreators.Client.clientItem.AnonymizeData(id)),
    onResetDeleteState: () => dispatch(RootActionCreators.Client.clientItem.ResetDeleteState()),
    onLoadFiles: (clientId: string) => dispatch(RootActionCreators.Client.clientFilesList.FetchData(clientId))
});

// Actual Component
export type AllProps = IProps & IDispatch & IStateProps & IDispatchProps & IPropsFromParams;
export const ClientDetails = (props: AllProps) => {
    const { t: tClient } = useTranslation('client');
    const { t: tCommon } = useTranslation('common');

    const loadData = () => props.onLoadData(props.match.params.id);

    React.useEffect(() => {
        const id = props.match.params.id;
        if (props.details.state === FetchState.NotFetched || (props.details.state === FetchState.Success && props.details.data.streamId !== id)) {
            props.onLoadData(id);
            props.onLoadFiles(id); // Load files for this client
        }
    }, [props.match.params.id, props.details.state, props.details.data.streamId]);

    const loading = props.details.state !== FetchState.Success;
    const isError = props.details.state === FetchState.Error;
    const hasFiles = props.clientFiles.state === FetchState.Success && props.clientFiles.data.length > 0;

    return <ClientNavigation navigations={props.navigations} selected={ClientNavigationPage.Details}>
        <PageHeader
                title={tClient('title')}
                subTitle={tClient('detailTitle', props.details.data)}
                explainText={tClient('detailsExplainText')}
            />
        <ClientDetail
          data={props.details.data}
          loading={loading}
          actions={<>
                <RefreshButton onClick={loadData} disabled={props.details.state === FetchState.Busy} />
                <EditButton onClick={() => props.onEdit(props.details.data.streamId)} disabled={loading} />
                <DeleteButton 
                    onClick={() => props.onDelete(props.details.data.streamId)} 
                    disabled={loading || hasFiles}  
                    deleteWarningMessage={hasFiles 
                        ? tClient('cannotDeleteHasFiles')
                        : tClient('deleteWarning', props.details.data)
                    } 
                />
            </>}
          />
        {isError && <ErrorMessage 
            errorDetails={props.details.errorMessage} 
            message={tCommon('errorLoadingData')} 
            title={tCommon('errorLoadingDataTitle')} 
            options={[
                {optionLabel: tCommon('tryAgain'), optionExecute: loadData},
                {optionLabel: tCommon('restart'), optionExecute: () => window.location.reload()}
            ]} 
        />}

        {props.deleter.state === FetchState.Busy && !isError && <DeletingMessage entity={tClient('client')} />}
        {props.deleter.state === FetchState.Error && !isError && <ErrorMessage 
            title={tCommon('deletingErrorTitle')} 
            message={tCommon('deletingErrorXMessage', {x: props.deleter.errorMessage})} 
            canClose 
            options={[]} 
            errorDetails={JSON.stringify(props.details.data)} 
        />}
    </ClientNavigation>;
};

export default connect<IStateProps, IDispatchProps, {}, IApplicationState>(mapStateProps, mapDispatchProps)(ClientDetails);

